import React, { useState, useEffect, useMemo, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AlertCircle,
  X,
  Home,
  Trophy,
  Menu,
  XCircle,
  Vote,
  Gift,
  HelpCircle,
  Lightbulb,
  RefreshCw,
} from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import { loadFromLocal, saveToLocal, cn } from '../../lib/utils'
import { SPONSOR_MARK_IMG_FILTER, sponsorMarkPanelClass } from '../../lib/sponsorMarkStyle'
import TutorialModal from './TutorialModal'
import usePendingScoresSync from '../../hooks/usePendingScoresSync'

/** Map route pathname -> tutorial page key (must match TUTORIAL_CONTENT keys). */
function tutorialPageFromPath(pathname) {
  if (pathname === '/dashboard') return 'dashboard'
  if (pathname.startsWith('/play/')) return 'play'
  if (pathname === '/leaderboard') return 'leaderboard'
  if (pathname === '/voting') return 'voting'
  if (pathname === '/raffle') return 'raffle'
  return null
}

export default function Layout({ children, showNav = true }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [alert, setAlert] = useState(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [currentTeam, setCurrentTeam] = useState(null)
  const [tutorialReplayKey, setTutorialReplayKey] = useState(0)
  const [longBgHintOpen, setLongBgHintOpen] = useState(false)
  const shellRef = useRef(null)
  const tutorialPage = useMemo(() => tutorialPageFromPath(location.pathname), [location.pathname])

  const hardRefresh = () => {
    window.location.reload()
  }

  useEffect(() => {
    const onLongBackground = () => {
      try {
        if (sessionStorage.getItem('puttzin_bg_hint_dismissed') === '1') return
      } catch {
        return
      }
      setLongBgHintOpen(true)
    }
    window.addEventListener('puttzin-long-background-visible', onLongBackground)
    return () => window.removeEventListener('puttzin-long-background-visible', onLongBackground)
  }, [])

  // Retry offline-queued score submissions on reconnect / tab-focus.
  usePendingScoresSync()

  // Subtle parallax on the tie-dye shell (CSS vars consumed in textures.css).
  // Intentionally scroll-driven only: the shell has no always-on animations, so
  // when the user isn't touching the screen the GPU does no shell work at all.
  useEffect(() => {
    const el = shellRef.current
    if (!el) return

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')

    const applyScroll = () => {
      if (!shellRef.current) return
      if (motionQuery.matches) {
        shellRef.current.style.removeProperty('--td-parallax-x')
        shellRef.current.style.removeProperty('--td-parallax-y')
        shellRef.current.style.removeProperty('--td-rotate')
        return
      }
      const y = window.scrollY
      // Cap rotation; square shell asset + CSS overscan keep edges covered on long scroll.
      const rotateDeg = Math.min(26, y * 0.012)
      shellRef.current.style.setProperty('--td-parallax-y', `${y * 0.055}px`)
      shellRef.current.style.setProperty('--td-parallax-x', `${Math.sin(y * 0.0028) * 10}px`)
      shellRef.current.style.setProperty('--td-rotate', `${rotateDeg}deg`)
    }

    let raf = 0
    const onScroll = () => {
      // Skip work if the tab is backgrounded — saves battery when the user locks
      // their phone with the page still open.
      if (typeof document !== 'undefined' && document.hidden) return
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(applyScroll)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    motionQuery.addEventListener('change', applyScroll)
    applyScroll()

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      motionQuery.removeEventListener('change', applyScroll)
    }
  }, [])

  useEffect(() => {
    // Load current team
    const team = loadFromLocal('currentTeam')
    setCurrentTeam(team)

    const onPendingScoresFailed = (event) => {
      const holeLabel = event.detail?.holeId ? ` for Hole ${event.detail.holeId}` : ''
      setAlert({
        message: `A saved score${holeLabel} could not sync. Please check with event staff.`,
      })
      setTimeout(() => setAlert(null), 12000)
    }
    window.addEventListener('pending-scores-sync-failed', onPendingScoresFailed)

    const annChannel = supabase
      .channel('announcements-live')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'announcements' },
        ({ new: row }) => {
          if (row?.active && row?.message) {
            setAlert({ message: row.message })
            setTimeout(() => setAlert(null), 12000)
          }
        }
      )
      .subscribe()

    return () => {
      window.removeEventListener('pending-scores-sync-failed', onPendingScoresFailed)
      supabase.removeChannel(annChannel)
    }
  }, [])

  const isActive = (path) => location.pathname === path

  const navItems = [
    { path: '/dashboard', label: 'Scorecard', icon: Home },
    { path: '/raffle', label: 'Raffle', icon: Gift },
    { path: '/voting', label: 'Vote', icon: Vote },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
    { path: '/help', label: 'Help', icon: HelpCircle },
  ]

  const handleNavClick = (path) => {
    navigate(path)
    setMobileMenuOpen(false)
  }

  /** Reset the "seen" flag for the current page tutorial and re-trigger the modal. */
  const handleReplayTips = () => {
    if (!tutorialPage) return
    const team = loadFromLocal('currentTeam')
    const rawTeamId = team?.id ?? 'guest'
    const teamId = String(rawTeamId).replace(/[^a-zA-Z0-9_-]/g, '_')
    const seenKey = `tutorial_seen_${teamId}`
    const skipAllKey = `tutorial_skip_all_${teamId}`
    const seenMap = loadFromLocal(seenKey) || {}
    const nextSeen = { ...seenMap }
    delete nextSeen[tutorialPage]
    saveToLocal(seenKey, nextSeen)
    saveToLocal(skipAllKey, false)
    setMobileMenuOpen(false)
    setTutorialReplayKey((k) => k + 1)
  }

  // Don't show nav on login page (either '/' or '/login')
  const shouldShowNav =
    showNav && location.pathname !== '/' && location.pathname !== '/login'
  const isDemoMode = Boolean(currentTeam?.isDemo)

  const isDisplayRoute = location.pathname.startsWith('/display')

  return (
    <div
      ref={shellRef}
      className="min-h-screen flex flex-col texture-tiedye text-white"
      style={{ paddingTop: 'var(--safe-area-inset-top)' }}
    >
      {/* Global Alert Banner */}
      <AnimatePresence>
        {alert && (
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            className="fixed top-0 left-0 right-0 z-50 bg-gradient-psych p-4 shadow-neon-lg border-b-4 border-white"
          >
            <div className="container mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-white" />
                <p className="text-white font-setlist font-bold text-lg uppercase">{alert.message}</p>
              </div>
              <button 
                type="button"
                onClick={() => setAlert(null)}
                aria-label="Dismiss announcement"
                className="text-white hover:text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-psych-purple rounded"
              >
                <X className="w-6 h-6" aria-hidden />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation Bar */}
      {shouldShowNav && (
        <nav data-tutorial="main-nav" className="sticky top-0 z-40 bg-panel border-b-4 border-neon-cyan shadow-neon" style={{ backgroundColor: '#111111' }}>
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between min-h-[5rem] sm:min-h-[5.25rem] py-2">
              {/* Logo/Team Name — tall enough to read on small screens */}
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 sm:gap-3 hover:opacity-90 transition-opacity min-w-0 flex-1 md:flex-initial"
              >
                <img
                  src="/brand/puttzin-logo.svg"
                  alt="Puttzin' Around"
                  className="h-14 sm:h-16 md:h-[4.5rem] w-auto max-w-[min(340px,78vw)] object-contain object-left shrink-0"
                  width={340}
                  height={136}
                  decoding="async"
                />
                {currentTeam && (
                  <div className="hidden md:flex items-center gap-2 min-w-0">
                    <span className="font-specs text-xs sm:text-sm text-platinum truncate">
                      | {currentTeam.name}
                    </span>
                    {isDemoMode && (
                      <span className="rounded-full border border-neon-cyan/70 bg-neon-cyan/15 px-2 py-0.5 font-specs text-[10px] uppercase tracking-wider text-neon-cyan">
                        Demo mode
                      </span>
                    )}
                  </div>
                )}
              </button>

              {/* Desktop Navigation - Sticker Buttons */}
              <div className="hidden md:flex items-center gap-2">
                {navItems.map(({ path, label, icon: Icon }) => (
                  <motion.button
                    key={path}
                    onClick={() => handleNavClick(path)}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className={`sticker flex items-center gap-2 px-4 py-2 font-setlist font-bold uppercase
                              transition-all border-3 ${
                      isActive(path)
                        ? 'bg-gradient-psych text-white border-white shadow-sticker-hover'
                        : 'bg-panel text-white border-gray-700 hover:border-neon-cyan shadow-sticker'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </motion.button>
                ))}
              </div>

              {/* Mobile Menu Button - Stomp Box Style */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
                aria-controls="puttzin-mobile-menu"
                className={`stomp-box stomp-box-nav md:hidden pl-4 pr-2.5 py-2.5 ${mobileMenuOpen ? 'active' : ''}`}
              >
                {mobileMenuOpen ? (
                  <XCircle className="w-6 h-6" aria-hidden />
                ) : (
                  <Menu className="w-6 h-6" aria-hidden />
                )}
              </button>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
              {mobileMenuOpen && (
                <motion.div
                  id="puttzin-mobile-menu"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="md:hidden overflow-hidden border-t border-gray-700 bg-panel"
                  style={{ backgroundColor: '#111111' }}
                >
                  <div className="py-4 space-y-2">
                    {currentTeam && (
                      <div className="px-4 py-2 font-specs text-sm text-platinum border-b border-gray-700 mb-2">
                        Team: {currentTeam.name}
                        {isDemoMode && (
                          <span className="ml-2 inline-flex rounded-full border border-neon-cyan/70 bg-neon-cyan/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-neon-cyan">
                            Demo mode
                          </span>
                        )}
                      </div>
                    )}
                    {navItems.map(({ path, label, icon: Icon }) => (
                      <button
                        key={path}
                        onClick={() => handleNavClick(path)}
                        className={`w-full flex items-center gap-3 px-4 py-3 font-setlist font-bold uppercase
                                  transition-all border-l-4 ${
                          isActive(path)
                            ? 'bg-gradient-psych/20 text-neon-cyan border-neon-cyan'
                            : 'bg-transparent text-white border-transparent hover:bg-panel'
                        }`}
                      >
                        <Icon className="w-5 h-5" aria-hidden />
                        {label}
                      </button>
                    ))}
                    {tutorialPage && (
                      <button
                        type="button"
                        onClick={handleReplayTips}
                        className="mt-2 w-full flex items-center gap-3 px-4 py-3 font-setlist font-bold uppercase border-l-4 border-transparent text-gray-300 hover:bg-panel hover:text-neon-cyan transition-all"
                      >
                        <Lightbulb className="w-5 h-5" aria-hidden />
                        Replay tips
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setMobileMenuOpen(false)
                        hardRefresh()
                      }}
                      className="mt-2 w-full flex items-center gap-3 px-4 py-3 font-setlist font-bold uppercase border-l-4 border-neon-cyan/40 text-neon-cyan hover:bg-panel transition-all"
                    >
                      <RefreshCw className="w-5 h-5" aria-hidden />
                      Refresh app
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <main
        className={cn(
          'flex-1 w-full',
          alert && 'pt-20',
          shouldShowNav
            ? cn(
                'md:pb-6',
                longBgHintOpen
                  ? // Mobile: tab bar + fixed hint card — reserve space so dashboard isn’t covered
                    'max-md:pb-64'
                  : 'max-md:pb-24'
              )
            : 'pb-4'
        )}
      >
        {children}
      </main>

      <AnimatePresence>
        {longBgHintOpen && shouldShowNav && (
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            role="status"
            className="md:hidden fixed left-2 right-2 z-[42] rounded-lg border-2 border-neon-cyan/80 bg-panel/98 p-3 shadow-neon-lg"
            style={{
              bottom: 'calc(5.35rem + env(safe-area-inset-bottom, 0px))',
            }}
          >
            <p className="font-specs text-sm text-white mb-2">
              Away a while? If the screen is blank after Maps or another app, refresh your page — your team login stays on this device.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={hardRefresh}
                className="flex-1 min-h-[44px] rounded-lg border-2 border-neon-cyan bg-neon-cyan/15 px-3 py-2 font-setlist text-sm font-bold uppercase text-neon-cyan"
              >
                Refresh
              </button>
              <button
                type="button"
                onClick={() => {
                  try {
                    sessionStorage.setItem('puttzin_bg_hint_dismissed', '1')
                  } catch {
                    // ignore
                  }
                  setLongBgHintOpen(false)
                }}
                className="min-h-[44px] rounded-lg border-2 border-white/30 px-3 py-2 font-setlist text-sm font-bold uppercase text-gray-200"
              >
                Dismiss
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Bottom Tab Bar — primary nav on small screens */}
      {shouldShowNav && (
        <nav
          aria-label="Primary"
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 border-t-2 border-neon-cyan/60 bg-panel/95 backdrop-blur-md"
          style={{
            backgroundColor: 'rgba(17,17,17,0.95)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)'
          }}
        >
          <ul className="grid grid-cols-5">
            {navItems.map(({ path, label, icon: Icon }) => {
              const active = isActive(path)
              return (
                <li key={path}>
                  <button
                    type="button"
                    onClick={() => handleNavClick(path)}
                    aria-current={active ? 'page' : undefined}
                    aria-label={label}
                    className={cn(
                      'w-full min-h-[60px] flex flex-col items-center justify-center gap-0.5 px-1 py-2 font-setlist text-[11px] font-bold uppercase tracking-wide transition-colors',
                      active
                        ? 'text-neon-cyan'
                        : 'text-gray-300 hover:text-white'
                    )}
                  >
                    <Icon className={cn('w-6 h-6', active && 'drop-shadow-[0_0_6px_rgba(0,173,239,0.6)]')} aria-hidden />
                    <span className="leading-none">{label}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        </nav>
      )}

      {/* Organizer + sponsor credits — dark neon panels + inverted marks (same system as SponsorLogo). TV display routes use header marks in DisplayPageHeader instead. */}
      <footer
        role="contentinfo"
        className={cn(
          'mt-auto w-full border-t border-white/15 bg-black/25 backdrop-blur-[2px]',
          isDisplayRoute && !shouldShowNav && 'hidden'
        )}
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}
      >
        {shouldShowNav && (
          <div className="flex flex-wrap items-center justify-center gap-2 border-b border-white/10 px-3 py-3 sm:px-4">
            <button
              type="button"
              onClick={hardRefresh}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border-2 border-neon-cyan/60 bg-panel/90 px-4 py-2 font-setlist text-xs font-bold uppercase tracking-wide text-neon-cyan hover:border-neon-cyan hover:bg-neon-cyan/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]"
              aria-label="Refresh app — fixes a frozen or blank screen after switching apps"
            >
              <RefreshCw className="h-4 w-4 shrink-0" aria-hidden />
              Refresh app
            </button>
            <span className="font-specs text-[11px] text-gray-500 max-w-[220px] text-center sm:max-w-none">
              Use if the page stays white after Maps.
            </span>
          </div>
        )}
        {!isDisplayRoute && (
          <div className="container mx-auto px-3 py-6 sm:px-4 sm:py-8">
            <div className="mx-auto flex max-w-5xl flex-row items-start justify-center gap-3 sm:gap-8 md:gap-12">
              <div className="flex min-w-0 flex-1 flex-col items-center gap-2 text-center sm:gap-3">
                <p className="font-setlist px-0.5 text-[10px] uppercase tracking-wide text-white/75 sm:text-xs sm:tracking-wider">
                  Presented by
                </p>
                <div className={cn('w-full px-3 py-5 sm:px-6 sm:py-7', sponsorMarkPanelClass)}>
                  <img
                    src="/sponsors/main-st-logo.svg"
                    alt="Dubuque Main Street"
                    className="mx-auto h-24 w-full max-h-44 object-contain object-center sm:h-28 sm:max-h-52 md:h-32 md:max-h-56"
                    style={{ filter: SPONSOR_MARK_IMG_FILTER }}
                    width={320}
                    height={166}
                    decoding="async"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="flex min-w-0 flex-1 flex-col items-center gap-2 text-center sm:gap-3">
                <p className="font-setlist px-0.5 text-[10px] uppercase leading-tight tracking-wide text-white/75 sm:text-xs sm:leading-normal sm:tracking-wider">
                  Production Sponsor
                </p>
                <div className={cn('w-full px-3 py-5 sm:px-6 sm:py-7', sponsorMarkPanelClass)}>
                  <img
                    src="/sponsors/umb-logo.svg"
                    alt="UMB Bank"
                    className="mx-auto h-24 w-full max-h-44 object-contain object-center sm:h-28 sm:max-h-52 md:h-32 md:max-h-56"
                    style={{ filter: SPONSOR_MARK_IMG_FILTER }}
                    width={320}
                    height={78}
                    decoding="async"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {shouldShowNav && (
          <div
            aria-hidden
            className="md:hidden"
            style={{ height: 'calc(5.25rem + env(safe-area-inset-bottom, 0px))' }}
          />
        )}
      </footer>

      {/* First-run tutorial / replayable tips — mounted once per route */}
      {tutorialPage && (
        <TutorialModal key={`${tutorialPage}-${tutorialReplayKey}`} page={tutorialPage} />
      )}
    </div>
  )
}
